/**
 * Photographs waiting for a connection.
 *
 * ── Why the blobs are not in the outbox ──────────────────────────────────────
 *
 * The outbox holds small JSON records and has to be cheap to read, rewrite and
 * subscribe to on every tap. A photo is 300–500kB of binary. Keeping the two in
 * one record means rewriting half a megabyte every time somebody presses PASS,
 * and it means the outbox cannot fall back to localStorage on a device where
 * IndexedDB is unavailable. So: bytes here, keyed by the entry's `client_id`;
 * the outbox item carries the KEY.
 *
 * ── The caps, and why nothing is ever silently dropped ───────────────────────
 *
 * §J asks that photos upload when the connection returns. It does not ask us to
 * hold an unbounded amount of binary in a browser store the operating system is
 * allowed to evict. 25 photos / 50MB is roughly two thorough inspections, warns
 * at 15, and REFUSES beyond the cap with a message naming the number.
 *
 * Refusing is the deliberate part. The obvious alternative — drop the oldest to
 * make room — loses a photograph somebody took of a rotten sill, at the moment
 * they can least afford to be told nothing. A refusal is visible and recoverable
 * (find signal, drain the queue, carry on). A silent eviction is neither.
 *
 * `navigator.storage.persist()` is requested when Inspection Mode opens, because
 * without it a browser under storage pressure may clear the origin's data
 * outright, and the field is exactly where that is unrecoverable.
 */

const DB_NAME = 'hh-field'
const DB_VERSION = 1

/**
 * TWO stores in ONE database, on purpose.
 *
 * Two databases means two independent `onupgradeneeded` version negotiations
 * racing on first open, in a tab that may also have a service worker running.
 * One database, one upgrade, both stores created together.
 *
 * `outbox` is created here and used by `outbox.ts`, which imports the helpers
 * below rather than opening its own connection.
 */
export const PHOTO_STORE = 'photos'
export const OUTBOX_STORE = 'outbox'

export const MAX_PHOTOS = 25
export const MAX_BYTES = 50 * 1024 * 1024
export const WARN_AT_PHOTOS = 15

export interface QueuedPhoto {
  /** The entry's `client_id`. The outbox item points at this. */
  key: string
  blob: Blob
  bytes: number
  width: number
  height: number
  /** What it belongs to, so a stranded photo can be explained rather than just counted. */
  opportunityId: string | null
  itemKey: string | null
  createdAt: number
}

export type PhotoRefusal = 'too_many' | 'too_big' | 'no_store'

export class PhotoQueueFull extends Error {
  readonly code: PhotoRefusal
  constructor(code: PhotoRefusal, message: string) {
    super(message)
    this.name = 'PhotoQueueFull'
    this.code = code
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * The store
 * ────────────────────────────────────────────────────────────────────────── */

let dbPromise: Promise<IDBDatabase | null> | null = null

/**
 * Returns null rather than throwing when IndexedDB is missing or refuses.
 *
 * It is missing in a jsdom test run, and it THROWS on open in a Firefox private
 * window and in some embedded iOS webviews. A field application that white
 * screens because a storage API said no is worse than one that keeps the queue
 * in memory for this session and says so.
 */
export function openFieldDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null)
      return
    }
    let request: IDBOpenDBRequest
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION)
    } catch {
      // A private window can throw synchronously here.
      resolve(null)
      return
    }
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(PHOTO_STORE)) db.createObjectStore(PHOTO_STORE, { keyPath: 'key' })
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) db.createObjectStore(OUTBOX_STORE, { keyPath: 'id' })
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => resolve(null)
    request.onblocked = () => resolve(null)
  })
  return dbPromise
}

function run<T>(store: IDBObjectStore, request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
    store.transaction.onabort = () => reject(store.transaction.error ?? new Error('IndexedDB transaction aborted'))
  })
}

/** Read every row of a store. Null means "there is no IndexedDB here". */
export async function idbAll<T>(storeName: string): Promise<T[] | null> {
  const db = await openFieldDb()
  if (!db) return null
  try {
    const tx = db.transaction(storeName, 'readonly')
    const store = tx.objectStore(storeName)
    return await run(store, store.getAll() as IDBRequest<T[]>)
  } catch {
    // A store that was never created (an older DB version on this device)
    // reads as "nothing stored", which is the truth.
    return null
  }
}

export async function idbPut(storeName: string, value: unknown): Promise<boolean> {
  const db = await openFieldDb()
  if (!db) return false
  try {
    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)
    await run(store, store.put(value))
    return true
  } catch {
    return false
  }
}

export async function idbDelete(storeName: string, key: string): Promise<boolean> {
  const db = await openFieldDb()
  if (!db) return false
  try {
    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)
    await run(store, store.delete(key))
    return true
  } catch {
    return false
  }
}

/**
 * The fallback when there is no IndexedDB.
 *
 * In memory, for this page only. A Blob cannot go in localStorage, and a
 * base64 round trip would put 50MB of string into a 5MB quota — so there is no
 * durable fallback to offer, and pretending otherwise would be the lie. What it
 * buys is that Inspection Mode still WORKS in a private window: photos queue,
 * upload on the next flush, and `durable()` below reports false so the screen
 * can warn that closing the tab loses what has not gone up yet.
 */
const memory = new Map<string, QueuedPhoto>()

/** False when photos are being held in memory only — the screen must say so. */
export async function durable(): Promise<boolean> {
  return (await openFieldDb()) !== null
}

/* ────────────────────────────────────────────────────────────────────────────
 * The queue
 * ────────────────────────────────────────────────────────────────────────── */

export async function list(): Promise<QueuedPhoto[]> {
  const rows = await idbAll<QueuedPhoto>(PHOTO_STORE)
  const all = rows ?? [...memory.values()]
  return all.sort((a, b) => a.createdAt - b.createdAt)
}

export interface PhotoStats {
  count: number
  bytes: number
  /** True once the queue is long enough that somebody should go find signal. */
  warn: boolean
  full: boolean
}

export async function stats(): Promise<PhotoStats> {
  const all = await list()
  const bytes = all.reduce((sum, p) => sum + p.bytes, 0)
  return {
    count: all.length,
    bytes,
    warn: all.length >= WARN_AT_PHOTOS,
    full: all.length >= MAX_PHOTOS || bytes >= MAX_BYTES,
  }
}

/**
 * Queue one photograph. Throws `PhotoQueueFull` rather than dropping anything.
 *
 * Re-putting the same key is a replace, not a second row: a retry of the same
 * tap must not consume two slots.
 */
export async function put(photo: Omit<QueuedPhoto, 'bytes' | 'createdAt'> & { createdAt?: number }): Promise<QueuedPhoto> {
  const existing = await list()
  const isReplacement = existing.some((p) => p.key === photo.key)
  if (!isReplacement) {
    if (existing.length >= MAX_PHOTOS) {
      throw new PhotoQueueFull('too_many', `${MAX_PHOTOS} photos are already waiting to upload. Find a signal and let them go up first.`)
    }
    const bytes = existing.reduce((sum, p) => sum + p.bytes, 0)
    if (bytes + photo.blob.size > MAX_BYTES) {
      throw new PhotoQueueFull('too_big', `There is no room for another photo — ${Math.round(MAX_BYTES / 1024 / 1024)}MB are already waiting. Find a signal and let them go up first.`)
    }
  }

  const row: QueuedPhoto = {
    key: photo.key,
    blob: photo.blob,
    bytes: photo.blob.size,
    width: photo.width,
    height: photo.height,
    opportunityId: photo.opportunityId,
    itemKey: photo.itemKey,
    createdAt: photo.createdAt ?? Date.now(),
  }

  const stored = await idbPut(PHOTO_STORE, row)
  if (!stored) memory.set(row.key, row)
  return row
}

export async function get(key: string): Promise<QueuedPhoto | null> {
  const all = await list()
  return all.find((p) => p.key === key) ?? null
}

/** Called only after the bytes are safely on the server. */
export async function remove(key: string): Promise<void> {
  const removed = await idbDelete(PHOTO_STORE, key)
  if (!removed) memory.delete(key)
}

/**
 * Ask the browser not to evict this origin under storage pressure.
 *
 * Chrome grants it silently for an installed PWA; Safari does not implement it
 * at all. Either way the answer is informational — there is nothing useful to do
 * with a "no" except report it, which is what the screen does.
 */
export async function requestPersistence(): Promise<boolean> {
  try {
    if (typeof navigator === 'undefined' || !navigator.storage?.persist) return false
    if (await navigator.storage.persisted?.()) return true
    return await navigator.storage.persist()
  } catch {
    // Some webviews expose the method and throw on call.
    return false
  }
}

/** Test seam. Drops the in-memory fallback and forgets the open connection. */
export function _resetForTests(): void {
  memory.clear()
  dbPromise = null
}
